import { useCallback, useState } from 'react';
import { exportAcknowledgementAuditTrail } from '../services/acknowledgementAuditTrailService';
import type {
  AuditTrailExportFormat,
  AuditTrailSearchFilters,
} from '../types/acknowledgementAuditTrail.types';

interface UiNotificationService {
  show: (options: {
    title: string;
    message: string;
    type: 'error' | 'success' | 'info' | 'warning';
    duration?: number;
  }) => void;
}

const FORMAT_LABELS: Record<AuditTrailExportFormat, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
  excel: 'Excel',
  pdf: 'PDF',
};

export function useAcknowledgementAuditTrailExport(
  uiNotificationService?: UiNotificationService | null
) {
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const handleExport = useCallback(
    async (format: AuditTrailExportFormat, filters: AuditTrailSearchFilters) => {
      const formatLabel = FORMAT_LABELS[format] || format.toUpperCase();
      setExportMenuOpen(false);
      setExporting(true);

      uiNotificationService?.show({
        title: 'Export in progress',
        message: `Generating ${formatLabel} export with current filters…`,
        type: 'info',
        duration: 3000,
      });

      try {
        await exportAcknowledgementAuditTrail(format, filters);

        uiNotificationService?.show({
          title: 'Export complete',
          message: `Your ${formatLabel} file download should begin shortly.`,
          type: 'success',
          duration: 4000,
        });
      } catch (error) {
        uiNotificationService?.show({
          title: 'Export failed',
          message: error instanceof Error ? error.message : 'Unable to export audit trail data',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setExporting(false);
      }
    },
    [uiNotificationService]
  );

  const toggleExportMenu = useCallback(() => {
    if (exporting) {
      return;
    }
    setExportMenuOpen(open => !open);
  }, [exporting]);

  return {
    exporting,
    exportMenuOpen,
    toggleExportMenu,
    handleExport,
  };
}
